import { useCallback, useEffect, useState } from "react";
import { Col, Container, Form, Row } from "react-bootstrap";
import { useForm } from "react-hook-form";
import { useNavigate } from "react-router-dom";
import { formatPhoneNumber } from "../../../../../../common/helpers/form-helper.js";
import {
  registerTradedDevice,
  validateTradeInRegistration,
} from "../../../../../../common/services/externalService.js";
import langs from "../../../../../../common/services/languageService.js";
import FormErrorMessage from "../../../../../ui/form-error-message/form-error-message.jsx";
const { labels, validationMessages } = langs;

function TradeInNewDevice({
  sourceAppType,
  requestProps,
  formSubmitProps,
  transactionId,
}) {
  const { isLoading, startRequest, finishRequest } = requestProps;
  const {
    setSubmitFn,
    handleSuccessfulSubmit,
    handleSubmitError,
    setButtonLabel,
    setButtonEnabled,
  } = formSubmitProps;
  const navigate = useNavigate();
  const {
    register,
    setValue,
    setError,
    formState: { errors, isValid, isSubmitted },
    handleSubmit,
  } = useForm({
    criteriaMode: "all",
    mode: "onChange",
  });
  const [showForm, setShowForm] = useState(false);

  useEffect(() => {
    setButtonLabel(labels.Save());
  }, [setButtonLabel]);

  useEffect(() => {
    setShowForm(!isLoading);
  }, [isLoading]);

  useEffect(() => {
    startRequest();
    validateTradeInRegistration(
      transactionId,
      sourceAppType,
    )
      .catch((err) => {
        console.error(err);
        navigate("/error");
      })
      .then((resp) => {
        if (!resp.data.transactionValid) {
          navigate("/error");
        }

        if (resp.data.linkExpired || resp.data.transactionCancelled) {
          navigate("/expired");
        }
      })
      .finally(() => finishRequest());
  }, []);

  const onSubmit = useCallback(
    (data) => {
      startRequest();

      const captchaKey = localStorage.getItem("captchaToken");
      localStorage.removeItem("captchaToken");

      var payload = {
        ...data,
        phoneNumber: 48 + data.phoneNumber.replaceAll(" ", ""),
        transactionId,
        captchaKey,
      };

      registerTradedDevice(payload, sourceAppType)
        .catch((err) => {
          console.error(err);
          navigate("/error");
        })
        .then((resp) => {
          finishRequest();
          let isRequestValid = true;
          if (resp.data.incorrectDeviceNumber) {
            setError("deviceNumber", {
              type: "server",
              message: validationMessages.InvalidDeviceNumber(),
            });
            isRequestValid = false;
          }

          if (resp.data.incorrectPhoneNumber) {
            setError("phoneNumber", {
              type: "server",
              message: validationMessages.EnterCorrectPhoneNumber(),
            });
            isRequestValid = false;
          }

          if (!isRequestValid) {
            handleSubmitError();
            return;
          }

          if (!resp.data.deviceRegistrationSuccessful) {
            navigate("/device-registration-failure");
            return;
          }

          handleSuccessfulSubmit();
        })
        .finally(() => {
          finishRequest();
        });
    },
    [handleSubmitError, handleSuccessfulSubmit],
  );

  const submitFn = useCallback(() => {
    return handleSubmit(onSubmit);
  }, [onSubmit, handleSubmit]);

  useEffect(() => {
    setSubmitFn(submitFn);
    return () => {
      setSubmitFn(null);
    };
  }, [submitFn]);

  useEffect(() => {
    setButtonEnabled(!isSubmitted || isValid);
  }, [setButtonEnabled, isValid, isSubmitted]);

  const handlePhoneNumberChange = (event) => {
    const inputNumber = event.target.value.replace(/\D/g, "");
    let formattedNumber = formatPhoneNumber(inputNumber);
    setValue("phoneNumber", formattedNumber, { shouldValidate: isSubmitted });
  };

  return (
    !showForm || (
      <Form className="ploom">
        <Container>
          <Row>
            <Col>
              <Form.Group>
                <Form.Label htmlFor="phoneNumber">
                  {labels.EnterPhoneNumber()}
                  <span>*</span>
                </Form.Label>
                <div className="phone-number">
                  <span className="phone-number--prefix">+48</span>
                  <Form.Control
                    id="phoneNumber"
                    inputMode="numeric"
                    {...register("phoneNumber", {
                      required: validationMessages.Required(),
                      pattern: {
                        value: /^\d{3} \d{3} \d{3}$/,
                        message: validationMessages.PhoneNumber(),
                      },
                    })}
                    placeholder="000 000 000"
                    onChange={handlePhoneNumberChange}
                  ></Form.Control>
                </div>
                <FormErrorMessage
                  formControlName={"phoneNumber"}
                  errors={errors}
                ></FormErrorMessage>
              </Form.Group>
            </Col>
          </Row>
          <Row>
            <Col>
              <Form.Group>
                <Form.Label htmlFor="deviceNumber">
                  {labels.EnterPloomAuraDeviceNumber()}
                  <span>*</span>
                </Form.Label>
                <Form.Control
                  id="deviceNumber"
                  {...register("deviceNumber", {
                    required: validationMessages.Required(),
                    pattern: {
                      value: /^(.{12}|.{15})$/,
                      message: validationMessages
                        .FixedLength()
                        .replace("{0}", "12 lub 15"),
                    },
                  })}
                />
                {isSubmitted && (
                  <FormErrorMessage
                    formControlName={"deviceNumber"}
                    errors={errors}
                  ></FormErrorMessage>
                )}
              </Form.Group>
            </Col>
          </Row>
          <Row>
            <Col className="d-flex align-items-center justify-content-center px-3">
              <img
                className="box w-100"
                style={{ maxWidth: "200px" }}
                src={process.env.PUBLIC_URL + "/img/package_bottom_code.png"}
                alt=""
              />
            </Col>
          </Row>
        </Container>
      </Form>
    )
  );
}

export default TradeInNewDevice;
